import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { toast } from 'react-toastify';

import useApplication from './Application';
import useEvaluation from './Evaluation';
import { getNext } from '../api.js';

const AnswerContext = React.createContext({ value: null, next: () => {} });

const utoast = (message, options) => {
  const toastId = 'answer-context';
  if (message === null) {
    toast.dismiss(toastId)
    return;
  }
  if (!toast.isActive(toastId)) {
    toast(message, {toastId, ...options});
  } else {
    toast.update(toastId, {
      render: message,
      ...options
    });
  }
}

const AnswerContextProvider = ({ children }) => {

  const { fetchEvaluation } = useEvaluation();
  const { application, clearApplication } = useApplication();

  const [state, setState] = useState(null);
  const correctingTimeoutRef = useRef(null);
  const lastSentAnswerId = useRef(null);
  // One `next` at a time: a double-click sent two, and the server made two answers of one
  // question (#437). The server serialises them too; this drops only a bare poll, never an answer.
  const inFlight = useRef(false);

  const clearState = () => {
    setState(null);
    lastSentAnswerId.current = null;
  };

  const next = useCallback(
    async (values = {}, paused = false) => {
      // Only a bare poll is dropped: a call carrying an answer or a pause is never lost.
      const carriesAnswer = paused || Object.keys(values || {}).length > 0;
      if (inFlight.current && !carriesAnswer) {
        return;
      }
      inFlight.current = true;

      if (correctingTimeoutRef.current) {
        clearTimeout(correctingTimeoutRef.current);
        correctingTimeoutRef.current = null;
      }

      utoast('Loading next question...', {
        isLoading: true,
        closeButton: false,
        autoClose: false,
      });

      try {
        const shouldSendAnswer = state?.eid && lastSentAnswerId.current !== state.eid;

        const formData = new FormData();
        formData.append('application', application.eid);
        if (shouldSendAnswer) {
          formData.append('answer', state.eid);
        }
        if (paused) {
          formData.append('paused', paused);
        }
        Object.entries(values || {}).forEach(([key, value]) => {
          if (value instanceof Blob) {
            formData.append(key, value, value.name || 'video.webm');
          } else if (value !== undefined && value !== null) {
            formData.append(key, value);
          }
        });

        const response = await getNext(formData);

        if (shouldSendAnswer) {
          lastSentAnswerId.current = state.eid;
        }

        switch (response.status) {
          case 'paused':
            clearState();
            clearApplication();
            fetchEvaluation();
            utoast(`The assessment '${application.assessment.title}' was paused.`, {
              type: 'success',
              isLoading: false,
              autoClose: 3000,
              closeButton: false,
            });
            break;

          case 'corrupted':
            clearState();
            clearApplication();
            fetchEvaluation();
            utoast(`The assessment '${application.assessment.title}' was corrupted. Reload and try again.`, {
              type: 'error',
              isLoading: false,
              autoClose: 3000,
              closeButton: false,
            });
            break;

          case 'notfound':
            clearState();
            clearApplication();
            fetchEvaluation();
            utoast(`The assessment '${application.assessment.title}' was not found. Reload and try again.`, {
              type: 'error',
              isLoading: false,
              autoClose: 3000,
              closeButton: false,
            });
            break;

          case 'correcting':
            utoast('Waiting for correction...', {
              isLoading: true,
              autoClose: false,
              closeButton: false,
            });
            if (!correctingTimeoutRef.current) {
              correctingTimeoutRef.current = setTimeout(() => {
                correctingTimeoutRef.current = null;
                next();
              }, 5000);
            }
            break;

          case 'completed':
            clearState();
            clearApplication();
            fetchEvaluation();
            utoast(`The assessment '${application.assessment.title}' was completed!`, {
              type: 'success',
              isLoading: false,
              autoClose: 3000,
              closeButton: false,
            });
            break;

          case 'ok':
            setState(response.answer);
            lastSentAnswerId.current = null;
            utoast(null);
            break;

          default:
            clearState();
            clearApplication();
            fetchEvaluation();
            utoast(`Unknown error, reload the page and try again.`, {
              type: 'error',
              isLoading: false,
              autoClose: 3000,
              closeButton: false,
            });
        }
      } catch (error) {
        utoast('An error has occurred. Reload the page to continue with the assessment.', {
          type: 'error',
          isLoading: false,
          autoClose: 3000,
          closeButton: false,
        });
        console.error(error);
      } finally {
        inFlight.current = false;
      }
    },
    [state, application, clearApplication, fetchEvaluation]
  );

  useEffect(() => {
    return () => {
      if (correctingTimeoutRef.current) {
        clearTimeout(correctingTimeoutRef.current);
        correctingTimeoutRef.current = null;
      }
    };
  }, []);

  const value = useMemo(
    () => ({
      answer: state,
      clearAnswer: clearState,
      nextAnswer: next,
    }),
    [state, next]
  );

  return (
    <AnswerContext.Provider value={value}>
      {children}
    </AnswerContext.Provider>
  );
};

const AnswerContextConsumer = AnswerContext.Consumer;

const useAnswer = () => useContext(AnswerContext);

export {
  useAnswer,
  AnswerContext,
  AnswerContextProvider,
  AnswerContextConsumer,
};

export default useAnswer;
